import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  Grid,
  Button,
  CircularProgress,
  Pagination,
  IconButton,
  Tooltip,
  InputAdornment,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Bell,
  Inbox,
  Search,
  CheckCircle,
  Trash2,
  RefreshCw,
  ArrowLeft,
  X,
  FilterX,
} from "lucide-react";
import NotificationCard from "./NotificationComponents/NotificationCard";
import { useNavigate } from "react-router-dom";
import BreadCrumbsContainer from "../Common/Breadcrumbs";

// Mock data for demonstration - replace with API call in production
const getMockNotifications = () => {
  return Array(30)
    .fill()
    .map((_, index) => ({
      id: `notif-${index}`,
      title: `Notification ${index + 1}`,
      message:
        index % 3 === 0
          ? "Order #ORD-2023-1234 has been delivered to the customer."
          : index % 3 === 1
          ? "Payment received for Invoice #INV-2023-5678 in the amount of $1,250.00."
          : "Inventory level for product SKU-7890 is below threshold. Please restock.",
      timestamp: new Date(
        Date.now() - Math.floor(Math.random() * 30) * 24 * 60 * 60 * 1000,
      ).toISOString(),
      type:
        index % 6 === 0
          ? "order"
          : index % 6 === 1
          ? "payment"
          : index % 6 === 2
          ? "inventory"
          : index % 6 === 3
          ? "customer"
          : index % 6 === 4
          ? "request"
          : "alert",
      isRead: index % 4 !== 0,
      link:
        index % 3 === 0
          ? "/orders/details/123"
          : index % 3 === 1
          ? "/payments/details/456"
          : "/inventory",
    }));
};

const ITEMS_PER_PAGE = 10;

const ViewAllNotifications = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    type: "",
    readStatus: "",
    timeRange: "",
  });

  // Fetch notifications on component mount
  useEffect(() => {
    setLoading(true);
    // In a real app, this would be an API call
    setTimeout(() => {
      const data = getMockNotifications();
      setNotifications(data);
      setFilteredNotifications(data);
      setLoading(false);
    }, 1000); // Simulate API delay
  }, []);

  // Apply filters and search
  useEffect(() => {
    let result = [...notifications];

    // Apply type filter
    if (filters.type) {
      result = result.filter(
        notification => notification.type === filters.type,
      );
    }

    // Apply status filter
    if (filters.readStatus) {
      result = result.filter(
        notification =>
          (filters.readStatus === "read" && notification.isRead) ||
          (filters.readStatus === "unread" && !notification.isRead),
      );
    }

    // Apply time period filter
    if (filters.timeRange) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      result = result.filter(notification => {
        const notifDate = new Date(notification.timestamp);

        if (filters.timeRange === "today") {
          return notifDate >= today;
        } else if (filters.timeRange === "week") {
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return notifDate >= weekAgo;
        } else if (filters.timeRange === "month") {
          const monthAgo = new Date(today);
          monthAgo.setDate(monthAgo.getDate() - 30);
          return notifDate >= monthAgo;
        }

        return true;
      });
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        notification =>
          notification.title.toLowerCase().includes(query) ||
          notification.message.toLowerCase().includes(query),
      );
    }

    setFilteredNotifications(result);
    setPage(1); // Reset to first page when filters change
  }, [filters, searchQuery, notifications]);

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      type: "",
      readStatus: "",
      timeRange: "",
    });
    setSearchQuery("");
  };

  const handleMarkAsRead = notificationId => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notificationId ? { ...notif, isRead: true } : notif,
      ),
    );
  };

  const handleDeleteNotification = notificationId => {
    setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
  };

  const handleMarkAllAsRead = () => {
    setNotifications(prev => prev.map(notif => ({ ...notif, isRead: true })));
  };

  const handleDeleteAllRead = () => {
    setNotifications(prev => prev.filter(notif => !notif.isRead));
  };

  const handleRefresh = () => {
    setLoading(true);
    // In a real app, this would be an API call to refresh the data
    setTimeout(() => {
      const data = getMockNotifications();
      setNotifications(data);
      setFilteredNotifications(data);
      setLoading(false);
    }, 1000);
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredNotifications.length / ITEMS_PER_PAGE);
  const paginatedNotifications = filteredNotifications.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  // Count unread notifications
  const unreadCount = notifications.filter(notif => !notif.isRead).length;

  // Check if any filters are applied
  const hasAppliedFilters =
    searchQuery || filters.type || filters.readStatus || filters.timeRange;

  return (
    <div className="h-screen flex flex-col">
      <BreadCrumbsContainer />
      <div className="bg-white flex-1 overflow-hidden flex flex-col">
        {/* Header Section - Updated to match AllProducts.jsx */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            All Notifications
            {unreadCount > 0 && (
              <span className="ml-2 px-2 py-1 text-sm bg-red-100 text-red-600 rounded-full">
                {unreadCount} unread
              </span>
            )}
          </h1>

          {/* Search and Settings Section */}
          <div className="flex items-center gap-4">
            {/* Search Bar */}
            <div className="w-[550px]">
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 transform -translate-y-1/2"
                  size={18}
                  color="#83899F"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search notifications..."
                  className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                           focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                           outline-none transition-all text-gray-600"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:bg-gray-100 p-1 rounded-full">
                    <X size={16} color="#83899F" />
                  </button>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <Tooltip title="Mark all as read">
                <IconButton
                  onClick={handleMarkAllAsRead}
                  disabled={loading || unreadCount === 0}
                  className="hover:bg-gray-100">
                  <CheckCircle size={20} className="text-[#5652B0]" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete read notifications">
                <IconButton
                  onClick={handleDeleteAllRead}
                  disabled={loading || notifications.every(n => !n.isRead)}
                  className="hover:bg-gray-100">
                  <Trash2 size={20} className="text-red-500" />
                </IconButton>
              </Tooltip>

              <Tooltip title="Refresh notifications">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  className="hover:bg-gray-100">
                  <RefreshCw
                    size={20}
                    className={`text-gray-600 ${loading ? "animate-spin" : ""}`}
                  />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between ">
            <div className="flex items-center gap-4 flex-wrap">
              {/* Type Filter */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.type}
                  label="Type"
                  onChange={e => handleFilterChange("type", e.target.value)}
                  sx={{
                    height: 40,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5652B0",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5652B0",
                    },
                  }}>
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="order">Order</MenuItem>
                  <MenuItem value="payment">Payment</MenuItem>
                  <MenuItem value="inventory">Inventory</MenuItem>
                  <MenuItem value="customer">Customer</MenuItem>
                  <MenuItem value="request">Request</MenuItem>
                  <MenuItem value="alert">Alert</MenuItem>
                </Select>
              </FormControl>

              {/* Read Status Filter */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.readStatus}
                  label="Status"
                  onChange={e =>
                    handleFilterChange("readStatus", e.target.value)
                  }
                  sx={{
                    height: 40,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5652B0",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5652B0",
                    },
                  }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="read">Read</MenuItem>
                  <MenuItem value="unread">Unread</MenuItem>
                </Select>
              </FormControl>

              {/* Time Period Filter */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Time Period</InputLabel>
                <Select
                  value={filters.timeRange}
                  label="Time Period"
                  onChange={e =>
                    handleFilterChange("timeRange", e.target.value)
                  }
                  sx={{
                    height: 40,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5652B0",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5652B0",
                    },
                  }}>
                  <MenuItem value="">All Time</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">Last 7 Days</MenuItem>
                  <MenuItem value="month">Last 30 Days</MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* Clear Filters Button */}
            {hasAppliedFilters && (
              <button
                onClick={handleClearFilters}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 
                       hover:bg-gray-100 rounded-lg transition-colors">
                <FilterX size={16} />
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Notifications Content - Scrollable Area */}
        <div className="flex-1 overflow-auto p-6">
          {/* Notifications List */}
          <Paper
            sx={{
              width: "100%",
              boxShadow: "none",
              border: "1px solid #E5E7EB",
              borderRadius: "0.5rem",
              padding: 0,
            }}>
            {loading && paginatedNotifications.length === 0 ? (
              <div className="flex justify-center items-center py-16">
                <CircularProgress sx={{ color: "#5652B0" }} />
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Inbox size={48} color="#9CA3AF" />
                <Typography
                  variant="h6"
                  className="mt-4 mb-1 font-outfit font-medium text-gray-700">
                  No notifications found
                </Typography>
                <Typography
                  variant="body2"
                  className="text-gray-500 max-w-md mb-4">
                  {searchQuery
                    ? "No notifications match your search criteria"
                    : "You don't have any notifications matching the selected filters"}
                </Typography>
                {hasAppliedFilters && (
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    sx={{
                      borderColor: "#5652B0",
                      color: "#5652B0",
                      "&:hover": {
                        borderColor: "#433EA0",
                        backgroundColor: "rgba(86, 82, 176, 0.04)",
                      },
                    }}>
                    Clear Filters
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                  <Typography
                    variant="body2"
                    className="text-gray-600 font-outfit font-medium">
                    Showing {paginatedNotifications.length} of{" "}
                    {filteredNotifications.length} notifications
                  </Typography>
                </div>

                <div className="divide-y divide-gray-100">
                  {paginatedNotifications.map(notification => (
                    <div className="p-4" key={notification.id}>
                      <NotificationCard
                        notification={notification}
                        onMarkAsRead={() => handleMarkAsRead(notification.id)}
                        onDelete={() =>
                          handleDeleteNotification(notification.id)
                        }
                      />
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex justify-center py-4 border-t border-gray-200">
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      shape="rounded"
                      sx={{
                        "& .MuiPaginationItem-root": {
                          color: "#4B5563",
                        },
                        "& .Mui-selected": {
                          backgroundColor: "rgba(86, 82, 176, 0.1) !important",
                          color: "#5652B0",
                        },
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </Paper>
        </div>
      </div>

      {loading && (
        <div className="absolute top-0 left-0 right-0">
          <div className="h-1 w-full overflow-hidden">
            <div className="animate-progress bg-[#5652B0] h-1"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewAllNotifications;
